/** @format */

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import {
	getAllWarranties,
	getAllWarrantiesError,
	getAllWarrantiesSuccess,
	setSelectedWarranty,
	selectWarranty,
	autoSetSelectedWarranty
} from './warranties.actions';
import { of, switchMap } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { concatLatestFrom } from '@ngrx/operators';
import { selectAllProductSkus } from '../product-selection.selectors';
import { Store } from '@ngrx/store';
import { Logger } from '@shared/data-access/services';
import {
	selectIsResolvedSkuWarrantyEligible,
	selectSelectedWarranty,
	selectWarrantiesForResolvedSku
} from './warranties.selectors';
import { noopAction } from '@state/data-access';
import { setResolvedSku } from '../product-selection.actions';
import { NonMerchAvailabilityService } from '../../../../services/src/lib/non-merch/non-merch-availability.service';
import { AppSnackbarService } from '../../../../services/src/lib/app-snackbar.service';

@Injectable({ providedIn: 'root' })
export class WarrantiesEffects {
	private readonly actions$ = inject(Actions);
	private readonly store = inject(Store);
	private readonly nonMerchAvailabilityService = inject(NonMerchAvailabilityService);
	private readonly logger = inject(Logger);
	private readonly snackbarService = inject(AppSnackbarService);
	private readonly snackbarOptions = {
		duration: 4000,
		showAboveDrawers: true
	};

	getAllWarranties$ = createEffect(() =>
		this.actions$.pipe(
			ofType(getAllWarranties),
			concatLatestFrom(() => [this.store.select(selectAllProductSkus)]),
			switchMap(([, allProductSkus]) =>
				this.nonMerchAvailabilityService.getAvailableWarranties(allProductSkus).pipe(
					map((availableWarrantiesResponse) =>
						getAllWarrantiesSuccess({ availableWarrantiesResponse })
					),
					catchError((error) => {
						this.logger.error('Failed to get available warranties: ', error.message);
						return of(getAllWarrantiesError());
					})
				)
			)
		)
	);

	selectWarranty$ = createEffect(() =>
		this.actions$.pipe(
			ofType(selectWarranty),
			concatLatestFrom(() => [this.store.select(selectWarrantiesForResolvedSku)]),
			switchMap(([action, warranties]) => {
				if (action.selectedPlanSku === null) {
					// explicitly selected no plan
					return of(setSelectedWarranty({ selectedWarranty: null }));
				}
				const selectedWarranty = warranties?.find(
					(w) => w.planSku === action.selectedPlanSku
				);
				if (!selectedWarranty) {
					return of(noopAction());
				}
				return of(
					setSelectedWarranty({
						selectedWarranty: {
							planLength: selectedWarranty.planLength,
							planSku: selectedWarranty.planSku
						}
					})
				);
			})
		)
	);

	autoSetSelectedWarrantyOnSkuResolve$ = createEffect(() =>
		this.actions$.pipe(
			ofType(setResolvedSku),
			concatLatestFrom(() => [
				this.store.select(selectSelectedWarranty),
				this.store.select(selectWarrantiesForResolvedSku),
				this.store.select(selectIsResolvedSkuWarrantyEligible)
			]),
			switchMap(
				([
					action,
					previouslySelectedWarranty,
					warrantiesForResolvedSku,
					isResolvedSkuWarrantyElegible
				]) => {
					if (previouslySelectedWarranty === undefined || !action.resolvedSku) {
						// First warranty or sku was unresolved
						return of(noopAction());
					}

					// Deselect if resolved to an ineligible sku
					if (!isResolvedSkuWarrantyElegible) {
						this.snackbarService.showSnackbar(
							'Protection plan no longer available',
							this.snackbarOptions
						);
						return of(autoSetSelectedWarranty({ selectedWarranty: undefined }));
					}

					// Try to pick a plan of the same term length
					const warrantyToAutoSelect = warrantiesForResolvedSku.find(
						(w) => previouslySelectedWarranty?.planLength === w.planLength
					);
					if (warrantyToAutoSelect) {
						if (warrantyToAutoSelect?.planSku !== previouslySelectedWarranty?.planSku) {
							this.snackbarService.showSnackbar(
								'Protection plan selection updated',
								this.snackbarOptions
							);
						}
						return of(
							autoSetSelectedWarranty({
								selectedWarranty: {
									planLength: warrantyToAutoSelect.planLength,
									planSku: warrantyToAutoSelect.planSku
								}
							})
						);
					}

					// Deselect if none found
					if (previouslySelectedWarranty !== null) {
						this.snackbarService.showSnackbar(
							'Protection plan no longer available',
							this.snackbarOptions
						);
					}
					return of(autoSetSelectedWarranty({ selectedWarranty: undefined }));
				}
			)
		)
	);
}
